import Utils from "@utils/utils"
import axios from 'axios'

/**
 * 根据交付图命名规则格式化命名
 * 默认"$S-#"
 * 规则说明：
 * "$S" 代表货号
 * "#" 代表数字
 * 例1："$S-#" 按 货号-1、货号-2、货号-3 格式命名
 * 例2："#-AI" 按 1-AI、2-AI、3-AI 格式命名
 * 例3："AI-##" 按 AI-01、AI-02、AI-03 格式命名
 * ...
 * @param {*} param0 { skuCode, index }
 * @param {*} rule 
 * @returns 格式化后的文件名
 */
const formatFilenameByRule = ({
    skuCode, index
}, rule="$S-#") => {
    // 过滤掉不能作为文件名的字符 /\:*?"<>|
    const sanitizedRule = rule.replace(/[\/\\:*?"<>|]/g, '');
    // 替换货号占位符 $S
    let result = sanitizedRule.replace(/\$S/g, skuCode);
    // 匹配连续的 # 占位符，根据 # 的数量决定数字的补零位数
    result = result.replace(/#+/g, (match) => {
        const len = match.length;
        return String(index).padStart(len, '0');
    });
    return result;
}

const downloadAsZip = async (skuList, images, types, packageType, level1Folder, deliveryNamingRule, overflowCallback) => {
    try {
        let filenameList = []
        let level2Folder, level3Folder
        let deliveryImageIndex = 0
        // 多个sku
        for (let sku of skuList) {
            // 交付内容
            const deliveryImages = sku.deliveryImages
            const hasThisSku = deliveryImages.find(item => images.find(item2 => item2.imageId == item.imageId))
            if (!hasThisSku) continue
            // 商品编号
            // 交付批次下载时限制将批次名中的特殊字符替换成下划线
            const skuFolderName = Utils.sanitizeString(sku.skuCode || sku.skuNumber) // 没填商品货号，使用skuNumber作为skuid
            // 当前sku的文件总大小
            let skuByteLength = 0
            // 创建文件夹
            let currentFolder = null
            if (packageType == 0) { // 按商品分文件下载：批次/skuCode/素材+交付图
                level2Folder = level1Folder.folder(skuFolderName)
                currentFolder = level2Folder
                filenameList = []
                deliveryImageIndex = 0 // 重置交付图索引
            } else if (packageType == 1) { // 所有图片合并下载: 批次/素材+交付图
                currentFolder = level1Folder
            } else if (packageType == 2) { // 素材图单独文件夹: 批次/skuCode/素材、交付图
                level2Folder = level1Folder.folder(skuFolderName)
                level3Folder = level2Folder.folder('素材')
                currentFolder = level3Folder
                filenameList = []
                deliveryImageIndex = 0 // 重置交付图索引
            }

            // 源素材
            if (types.includes('origin') && (!sku.deliveryTypes || sku.deliveryTypes.includes('origin'))) {
                let skuImages = sku.skuImages?.filter(item => !!item) || []
                let skuImageMap = sku.skuImageMap || []
                if (skuImages.length == 0) { // 20260331 修复skuImages为空时，从deliveryImages中获取skuImage
                    skuImages = sku.deliveryImages.filter(item => item.skuImage).map(item => item.skuImage)
                    skuImageMap = [...sku.deliveryImages.filter(item => item.skuImage).map(item => ({url: item.skuImage, name: item.skuImageName})), ...skuImageMap].filter(item => item.name)
                }
                skuImages = [...new Set(skuImages)]

                for (let url of skuImages) {
                    const urlName = url.split('?')[0].split('/').pop().split('.').shift()
                    const name = skuImageMap.find(item => item.url == url)?.name || urlName
                    const response = await axios.get(url, { responseType: 'arraybuffer' })
                    const fileExtension = url.split('?')[0].split('.').pop()
                    const filename = `${name}.${fileExtension}`
                    if (filenameList.includes(filename)) {
                        const length = filenameList.filter(name => name == filename).length
                        currentFolder.file(`${name}(${length}).${fileExtension}`, response.data, { binary: true })
                    } else {
                        currentFolder.file(filename, response.data, { binary: true })
                    }
                    filenameList.push(filename)
                    // 累加文件大小
                    skuByteLength += response.data.byteLength
                }
            }
            // 搭配图
            if (types.includes('match') && (!sku.deliveryTypes || sku.deliveryTypes.includes('match'))) {
                let matchImages = sku.matchImages?.filter(item => !!item) || []
                let matchImageMap = sku.matchImageMap || []
                if (matchImages.length == 0) { // 20260331 修复matchImages为空时，从deliveryImages中获取matchClothImage
                    matchImages = sku.deliveryImages.filter(item => item.matchClothImage).map(item => item.matchClothImage)
                    matchImageMap = [...sku.deliveryImages.filter(item => item.matchClothImage).map(item => ({url: item.matchClothImage, name: item.matchClothName})), ...matchImageMap].filter(item => item.name)
                }
                matchImages = [...new Set(matchImages)]
                for (let url of matchImages) {
                    const urlName = url.split('?')[0].split('/').pop().split('.').shift()
                    const name = matchImageMap.find(item => item.url == url)?.name || urlName
                    const response = await axios.get(url, { responseType: 'arraybuffer' })
                    const fileExtension = url.split('?')[0].split('.').pop()
                    const filename = `${name}.${fileExtension}`
                    if (filenameList.includes(filename)) {
                        const length = filenameList.filter(name => name == filename).length
                        currentFolder.file(`${name}(${length}).${fileExtension}`, response.data, { binary: true })
                    } else {
                        currentFolder.file(filename, response.data, { binary: true })
                    }
                    filenameList.push(filename)
                    // 累加文件大小
                    skuByteLength += response.data.byteLength
                }
            }
            // 参考图
            if (types.includes('reference') && (!sku.deliveryTypes || sku.deliveryTypes.includes('reference'))) {
                let refImages = sku.refImages?.filter(item => !!item) || []
                if (refImages.length == 0) { // 20260331 修复refImages为空时，从deliveryImages中获取refImage
                    refImages = sku.deliveryImages.filter(item => item.refImage).map(item => item.refImage)
                }
                refImages = [...new Set(refImages)]
                for (let i = 0; i < refImages.length; i++) {
                    const url = refImages[i]
                    const response = await axios.get(url, { responseType: 'arraybuffer' })
                    const fileExtension = url.split('?')[0].split('.').pop()
                    const filename = `参考图${i+1}.${fileExtension}`
                    if (filenameList.includes(filename)) {
                        const length = filenameList.filter(name => name == filename).length
                        currentFolder.file(`参考图${i+1}(${length}).${fileExtension}`, response.data, { binary: true })
                    } else {
                        currentFolder.file(filename, response.data, { binary: true })
                    }
                    filenameList.push(filename)
                    // 累加文件大小
                    skuByteLength += response.data.byteLength
                }
            }
            // 交付图
            if (types.includes('delivery') && (!sku.deliveryTypes || sku.deliveryTypes.includes('delivery'))) {
                if (packageType == 2) {
                    currentFolder = level2Folder
                    filenameList = []
                }
                for (let i = 0; i < deliveryImages.length; i++) {
                    const delivery = deliveryImages[i]
                    const hasThis = images.find(item => item.imageId == delivery.imageId)
                    if (!hasThis) continue
                    if (hasThis.deliveryImage) {
                        const url = hasThis.deliveryImage
                        // 交付批次下载时限制将批次名中的特殊字符替换成下划线
                        const skuCode = Utils.sanitizeString(hasThis.skuCode)
                        const response = await axios.get(url, { responseType: 'arraybuffer' })
                        const fileExtension = url.split('?')[0].split('.').pop()
                        const filename = formatFilenameByRule({ skuCode, index: deliveryImageIndex+1 }, deliveryNamingRule)
                        const fullFilename = `${filename}.${fileExtension}`
                        if (filenameList.includes(fullFilename)) {
                            const length = filenameList.filter(name => name == fullFilename).length
                            currentFolder.file(`${filename}(${length}).${fileExtension}`, response.data, { binary: true })
                        } else {
                            currentFolder.file(fullFilename, response.data, { binary: true })
                        }
                        filenameList.push(fullFilename)
                        // 累加文件大小
                        skuByteLength += response.data.byteLength
                    }
                    deliveryImageIndex++
                }
            }

            // 分包下载
            const newZip = await overflowCallback(skuByteLength) 
            if (newZip) level1Folder = newZip
        }
    } catch (err) {
        console.error(err)
        throw err
    }
}

export { downloadAsZip }

